import { useEffect, useState, useRef, createContext, useContext, useCallback, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import Candidates from "./pages/Candidates";
import CandidateDetail from "./pages/CandidateDetail";
import CandidateDashboard from "./pages/CandidateDashboard";
import CandidateRegister from "./pages/CandidateRegister";
import ApplyPage from "./pages/ApplyPage";
import DemoTour from "./pages/DemoTour";
import ChangePassword from "./pages/ChangePassword";
import Institutions from "./pages/Institutions";
import IPCBs from "./pages/IPCBs";
import Competencies from "./pages/Competencies";
import Awards from "./pages/Awards";
import Analytics from "./pages/Analytics";
import Users from "./pages/Users";
import UserManagement from "./pages/UserManagement";
import Maintenance from "./pages/Maintenance";
import BadgeSettings from "./pages/BadgeSettings";
import StaffGuide from "./pages/StaffGuide";
import LoginPage from "./pages/LoginPage";
import Prospectus from "./pages/Prospectus";
import VerifyCredential from "./pages/VerifyCredential";
import WrittenInterviewScoring from "./pages/WrittenInterviewScoring";
import WrittenInterviewAnswer from "./pages/WrittenInterviewAnswer";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (intendedPath) => {
    // Save intended destination so we can redirect after auth
    if (intendedPath) {
      sessionStorage.setItem('auth_redirect', intendedPath);
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );
        const userData = response.data;
        setUser(userData);
        
        // Determine where to redirect based on saved path or user role
        const savedRedirect = sessionStorage.getItem('auth_redirect');
        sessionStorage.removeItem('auth_redirect');

        let target = '/dashboard';
        if (userData.must_change_password) {
          target = '/change-password';
        } else if (savedRedirect) {
          target = savedRedirect;
        } else if (userData.role === 'candidate') {
          target = userData.linked_candidate_id ? '/my-application' : '/apply';
        }

        // CRITICAL: Use hard navigation (replace) instead of SPA navigate.
        // This ensures the freshly-set session cookie is sent on the next
        // request and AuthProvider re-initialises with the authenticated user,
        // eliminating the first-load white-screen race condition.
        window.location.replace(target);
      } catch (error) {
        console.error('Auth error:', error);
        window.location.replace('/');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
        <p className="mt-4 text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route - with role-based access
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/" replace />;
  }

  const currentUser = user || location.state?.user;
  
  // If role restriction is specified, check it
  if (allowedRoles && !allowedRoles.includes(currentUser?.role)) {
    // Candidates should be redirected to their dashboard
    if (currentUser?.role === 'candidate') {
      return <Navigate to="/my-application" replace />;
    }
    // Others get access denied
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-4">You don't have permission to access this page.</p>
          <button 
            onClick={() => window.history.back()}
            className="text-blue-600 hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Candidate-only route
const CandidateRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const currentUser = user || location.state?.user;

  if (!currentUser) {
    // Save intended destination for post-login redirect
    sessionStorage.setItem('auth_redirect', '/my-application');
    return <Navigate to="/" replace />;
  }

  // If not a candidate, redirect to admin dashboard
  if (currentUser.role !== 'candidate') {
    return <Navigate to="/dashboard" replace />;
  }

  return <CandidateDashboard user={currentUser} onLogout={logout} />;
};

// App Router
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  // Check URL fragment for session_id synchronously during render
  // BUT skip this for /apply page - it handles its own OAuth callback for candidate registration
  if (location.hash?.includes('session_id=') && location.pathname !== '/apply') {
    return <AuthCallback />;
  }

  // Admin roles (not candidate)
  const adminRoles = ['super_admin', 'admin', 'assessor', 'iqa'];

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Apply Page - Self Registration */}
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/demo" element={<DemoTour />} />
      <Route path="/prospectus" element={<Prospectus />} />
      <Route path="/verify" element={<VerifyCredential />} />
      <Route path="/verify/:reportId" element={<VerifyCredential />} />

      {/* Dedicated email/password login page (linked from invitation emails) */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Change Password Page */}
      <Route path="/change-password" element={<ChangePassword />} />
      
      {/* Candidate Registration (legacy/invite flow) */}
      <Route path="/register" element={<CandidateRegister />} />
      
      {/* Candidate Self-Service Portal */}
      <Route path="/my-application" element={<CandidateRoute />} />
      
      {/* Admin Dashboard - redirects candidates to their portal */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={adminRoles}><Dashboard /></ProtectedRoute>
      } />
      
      {/* Candidate Management */}
      <Route path="/candidates" element={
        <ProtectedRoute allowedRoles={adminRoles}><Candidates /></ProtectedRoute>
      } />
      <Route path="/candidates/:id" element={
        <ProtectedRoute allowedRoles={adminRoles}><CandidateDetail /></ProtectedRoute>
      } />
      
      {/* Institution Management - Admin only */}
      <Route path="/institutions" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><Institutions /></ProtectedRoute>
      } />
      
      {/* IPCB Management - Admin only */}
      <Route path="/ipcbs" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><IPCBs /></ProtectedRoute>
      } />
      
      {/* Competencies - Admin and Assessor */}
      <Route path="/competencies" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'assessor']}><Competencies /></ProtectedRoute>
      } />
      
      {/* Awards - Admin only */}
      <Route path="/awards" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><Awards /></ProtectedRoute>
      } />
      
      {/* Analytics - All admin roles */}
      <Route path="/analytics" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'assessor', 'iqa']}><Analytics /></ProtectedRoute>
      } />
      
      {/* User Management - Super Admin only */}
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><Users /></ProtectedRoute>
      } />
      
      {/* Legacy users route - redirect to new user management */}
      <Route path="/user-management" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><Users /></ProtectedRoute>
      } />
      
      {/* Maintenance - Admin only */}
      <Route path="/maintenance" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin']}><Maintenance /></ProtectedRoute>
      } />

      {/* Badge Settings - Super Admin only */}
      <Route path="/badge-settings" element={
        <ProtectedRoute allowedRoles={['super_admin']}><BadgeSettings /></ProtectedRoute>
      } />

      {/* Staff Guide - All staff */}
      <Route path="/guide" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'assessor', 'iqa']}><StaffGuide /></ProtectedRoute>
      } />

      {/* Written Interview Scoring - Public (token-based auth) */}
      <Route path="/written-interview/score/:token" element={<WrittenInterviewScoring />} />

      {/* Written Interview Answer - Public (candidate token-based) */}
      <Route path="/written-interview/answer/:token" element={<WrittenInterviewAnswer />} />

      {/* Catch-all: any unknown URL goes back to landing instead of blank */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Top-level error boundary so a render error never leaves the user with a blank white screen
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App-level render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
          <div className="max-w-md text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Something went wrong</h2>
            <p className="text-slate-600 mb-4">
              The page failed to load. Please refresh, or return to the home page.
            </p>
            <pre className="text-xs text-left bg-slate-100 p-3 rounded text-slate-700 overflow-auto mb-4 max-h-32">
              {String(this.state.error?.message || this.state.error || 'Unknown error')}
            </pre>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800"
              >
                Refresh
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="px-4 py-2 border border-slate-300 rounded hover:bg-slate-100"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default App;
